import { api, ApiError } from "./api";
import { getSessionToken } from "./sessionToken";

export type PatientChatRequestKind =
  "note_rewrite" | "objective_data_summary" | "clinical_intelligence" | "prompt_reformat";

export interface PatientChatConversationTurn {
  role: "user" | "assistant";
  content: string;
}

export interface PatientChatDraftContext {
  note?: string;
  patientSummary?: string;
  diarizedTranscript?: string;
  noteCitations?: unknown;
  chartHistory?: unknown;
  includeFullChartHistory?: boolean;
}

export type PatientChatUploadedFileSourceType = "pdf" | "image" | "text";

export interface PatientChatUploadedFile {
  filename: string;
  mimeType: string;
  sourceType: PatientChatUploadedFileSourceType;
  sizeBytes: number;
  base64Data?: string;
  textContent?: string;
}

export type PatientChatQuickAction =
  "create_interval_history" | "reformat_section_order" | "make_concise";

export interface PatientChatMessageRequest {
  requestId?: string;
  requestKind?: PatientChatRequestKind;
  quickAction?: PatientChatQuickAction;
  message: string;
  useExternalEvidence?: boolean;
  /** Asks the server to end the reply with the AI Scribe consent statement. */
  includeConsentDisclaimer?: boolean;
  draftContext?: PatientChatDraftContext;
  uploadedFiles?: PatientChatUploadedFile[];
}

export interface PatientChatCitationFlags {
  usedTranscript: boolean;
  usedLabs: boolean;
  usedActiveProblems: boolean;
  usedObjectiveData?: boolean;
  usedUploadedFiles?: boolean;
  externalSources?: Array<{
    title: string;
    url: string;
    sourceType: "guideline" | "label" | "study" | "government" | "other";
    accessedAt?: string;
    citedText?: string;
  }>;
}

export interface PatientChatArtifact {
  type: string;
  [key: string]: unknown;
}

export interface PatientChatMessageResponse {
  runId: string | null;
  requestId?: string;
  userMessage?: string;
  sessionVersion?: number;
  requestKind: PatientChatRequestKind;
  reply: string;
  artifacts: PatientChatArtifact[];
  citations: PatientChatCitationFlags;
  mode: "stub" | "live";
}

export interface PatientChatStreamHandlers {
  onStart?: (requestKind: PatientChatRequestKind) => void;
  onDelta?: (text: string) => void;
  onProgress?: (message: string) => void;
}

export type PatientChatSessionStatus = "idle" | "pending" | "completed" | "failed";

export interface PatientChatCapabilitiesResponse {
  enabled: boolean;
  canRewriteNote: boolean;
  canSummarizeObjectiveData: boolean;
  canRunClinicalIntelligence: boolean;
  canUseExternalEvidence: boolean;
  canUploadObjectiveDataFiles: boolean;
  canApplyWrites: boolean;
  canCreateCustomPrompts?: boolean;
}

export interface PatientChatDeepgramTokenResponse {
  token: string;
  expiresIn: number;
  purpose: "ask-knowtex" | "additional-context";
  model: string;
  language: string;
}

export interface PatientChatDeepgramTokenRequest {
  purpose: "ask-knowtex" | "additional-context";
}

export interface PatientChatSessionResponse {
  sessionId: string;
  patientId: number;
  status: PatientChatSessionStatus;
  conversation: PatientChatConversationTurn[];
  draftPrompt: string;
  latestReply: string | null;
  latestCitations: PatientChatCitationFlags;
  latestRequestKind: PatientChatRequestKind | null;
  latestRunId: string | null;
  uploadedFiles: PatientChatUploadedFile[];
  lastError: string | null;
  version: number;
  updatedAt: string;
}

export interface PatientChatSessionSaveRequest {
  draftPrompt: string;
  expectedVersion?: number;
}

export const resolvePatientChatWorkingDraft = (
  latestReply: string | null | undefined,
  note: string | null | undefined
) => (latestReply?.trim() ? latestReply : note || "");

type PatientChatStreamEvent =
  | { type: "start"; requestKind: PatientChatRequestKind }
  | { type: "progress"; message: string; completed?: number; total?: number }
  | { type: "heartbeat" }
  | { type: "delta"; text: string }
  | { type: "complete"; response: PatientChatMessageResponse }
  | { type: "error"; error: string };

// Normalizes any error raised while stopped mid-stream to a real AbortError,
// so callers can reliably check `error.name === "AbortError"` regardless of
// whether the abort happened before `fetch` resolved or mid-body-read.
const createStreamAbortError = () =>
  typeof DOMException !== "undefined"
    ? new DOMException("Smart Assist request was stopped.", "AbortError")
    : Object.assign(new Error("Smart Assist request was stopped."), { name: "AbortError" });

const getStreamErrorMessage = (payload: unknown, fallback: string) => {
  if (payload && typeof payload === "object" && "error" in payload) {
    const error = (payload as { error?: unknown }).error;
    if (typeof error === "string" && error.trim()) {
      return error;
    }
  }

  return fallback;
};

const consumePatientChatStream = async (
  response: Response,
  handlers: PatientChatStreamHandlers,
  signal?: AbortSignal
): Promise<PatientChatMessageResponse> => {
  if (!response.body) {
    throw new ApiError("Smart Assist returned an empty stream.", response.status);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completedResponse: PatientChatMessageResponse | null = null;

  const handleLine = (line: string) => {
    if (!line.trim()) return;

    const event = JSON.parse(line) as PatientChatStreamEvent;
    if (event.type === "start") {
      handlers.onStart?.(event.requestKind);
      return;
    }

    if (event.type === "delta") {
      if (event.text) handlers.onDelta?.(event.text);
      return;
    }

    if (event.type === "progress") {
      if (event.message) handlers.onProgress?.(event.message);
      return;
    }

    if (event.type === "heartbeat") return;

    if (event.type === "complete") {
      completedResponse = event.response;
      return;
    }

    if (event.type === "error") {
      throw new ApiError(event.error || "Smart Assist stream failed.", response.status);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      lines.forEach(handleLine);
    }

    buffer += decoder.decode();
    handleLine(buffer);
  } catch (error) {
    // A Stop click aborts `signal` and rejects the pending `reader.read()`.
    // Surface that consistently as an AbortError instead of whatever error
    // shape the underlying fetch implementation happened to throw.
    if (signal?.aborted) {
      throw createStreamAbortError();
    }
    throw error;
  } finally {
    reader.releaseLock();
  }

  if (!completedResponse) {
    throw new ApiError("Smart Assist stream ended before completion.", response.status);
  }

  return completedResponse;
};

const sendPatientChatMessageStream = async (
  patientId: number,
  body: PatientChatMessageRequest,
  handlers: PatientChatStreamHandlers = {},
  signal?: AbortSignal
) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/patient-chat/patients/${patientId}/message/stream`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(getSessionToken()
          ? { token: getSessionToken() as string }
          : {}),
      },
      body: JSON.stringify(body),
      signal,
    }
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new ApiError(
      getStreamErrorMessage(payload, `Request failed with status ${response.status}`),
      response.status,
      payload
    );
  }

  return consumePatientChatStream(response, handlers, signal);
};

export const patientChatApi = {
  sendMessage: (patientId: number, body: PatientChatMessageRequest) =>
    api.post<PatientChatMessageResponse>(`/patient-chat/patients/${patientId}/message`, body),

  sendMessageStream: sendPatientChatMessageStream,

  getCapabilities: (patientId: number) =>
    api.get<PatientChatCapabilitiesResponse>(`/patient-chat/patients/${patientId}/capabilities`),

  getSession: (patientId: number) =>
    api.get<PatientChatSessionResponse>(`/patient-chat/patients/${patientId}/session`),

  saveSession: (patientId: number, body: PatientChatSessionSaveRequest) =>
    api.put<PatientChatSessionResponse>(`/patient-chat/patients/${patientId}/session`, body),

  clearSession: (patientId: number) =>
    api.delete<void>(`/patient-chat/patients/${patientId}/session`),

  createDeepgramToken: (body: PatientChatDeepgramTokenRequest) =>
    api.post<PatientChatDeepgramTokenResponse>("/patient-chat/deepgram-token", body),
};

export default patientChatApi;
